/**
 * The console's Workers tab: one room host at a time, as a thing an operator manages rather than
 * as a row in a list of processes.
 *
 * It exists because answering one question — "what have I promised this box, and can it hold
 * it?" — used to mean reading the Rooms & Processes tab and the Room roster tab side by side and
 * doing the arithmetic in your head. Everything about one host is here: what it is doing, what
 * has been assigned to it, the settings of each of those rooms, and its capacity knobs.
 *
 * ## THREE NUMBERS, AND THEY ARE NOT THE SAME NUMBER
 *
 * The whole page is built around keeping these apart, because an operator who conflates them
 * will provision the wrong box:
 *
 * - **COMMITTED** — the seats the declared rooms PINNED to this host would hold if they all
 *   filled. It is a promise made by saving a declaration, and it is knowable while the host is
 *   completely idle.
 * - **CURRENT** — the humans seated on it right now. `maxConcurrentPlayers` is enforced against
 *   this one and against nothing else.
 * - **UTILIZATION** — the share of its 50 ms tick the host is actually spending. A cost, not a
 *   count.
 *
 * The read-out is drawn on EVERY host, not only on locked ones. A lock closes a host to rooms
 * the matchmaker *invents*; it does not stop the declarations pinned there from waking, and an
 * auto host can be over-committed by its pins alone — with utilization at zero and nobody seated
 * — long before the matchmaker adds a dynamic room on top. That is exactly the case a purely
 * utilization-based view hides until it is live and too late.
 *
 * ## Nothing here is a second edit path
 *
 * Assigning and unassigning a declared room is `admin.rooms.update`, in the same shape and with
 * the same three-valued host field the Room roster tab sends — a host id pins, null unpins, an
 * absent field leaves the pin alone. A declared room's settings are that same mutation; a
 * dynamic room's are `admin.updateRoomSettings`, which is what the operations tab uses and which
 * evicts. The form itself is `SettingsForm`, the roster's own, inline instead of in a modal. The
 * capacity knobs are `CapacityDialog`, owned by the page and handed down. There is no rule about
 * a room or a host on this page that is not the rule everywhere else.
 */

import { type HostAllocation, hostAllocation } from '@chompdome/shared/constants';
import type { RoomSettings } from '@chompdome/shared/types';
import { type ReactNode, useState } from 'react';
import type { RosterRow } from '../../../../server/src/trpc/routers/admin-rooms.js';
import type { ProcessRow, RoomRow } from '../../../../server/src/trpc/routers/admin.js';
import { type RoomSettingsPatch, SettingsForm } from '@/components/admin/settings-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { trpc } from '@/lib/trpc';

/** An operator view, not a game loop; the same cadence as the rest of the console. */
const POLL_INTERVAL_MS = 5_000;

/** Above this share of the tick budget a host is close enough to falling behind to shout about. */
const UTILIZATION_ALARM = 0.9;

/** The `<select>` value that means "pick a room" — an option's value is a string, and null isn't. */
const NONE = '';

export interface WorkersPanelProps {
  /** Every room host, reachable or not — `admin.listProcesses`, polled by the page. */
  processes: readonly ProcessRow[];
  /** Every live room on every reachable host — `admin.listRooms`, polled by the page. */
  rooms: readonly RoomRow[];
  isLoading: boolean;
  /** Opens the page's own `CapacityDialog` on this host: one editor, wherever it is reached from. */
  onEditCapacity: (row: ProcessRow) => void;
}

/** One room in the selected host's list, and which of the three kinds of room it is. */
interface AssignedRoom {
  key: string;
  id: string;
  /**
   * What the room is called. Never null: a declaration's name, or — for a room the matchmaker
   * invented — the seeded one the host gave it (`roomNameFor`). The id is drawn under it.
   */
  name: string;
  /**
   * PINNED is the commitment: a declaration that names this host. AUTO is a declaration that
   * named none and happens to have landed here. DYNAMIC is a room the matchmaker invented. Only
   * the first counts toward COMMITTED, and all three are drawn differently.
   */
  kind: 'pinned' | 'auto' | 'dynamic';
  /** The settings it *opens* with (a declaration) or is running with (a live room). */
  settings: RoomSettings;
  /** The host actually running it, or null when nothing is. Never inferred from the pin. */
  runningOn: string | null;
  live: RoomRow['room'] | RosterRow['live'];
  /**
   * The declaration behind this room, when there is one. Null for an overflow room, which is
   * nobody's declaration and therefore has nothing to unassign and no roster row to edit.
   */
  declaration: RosterRow | null;
}

export function WorkersPanel({ processes, rooms, isLoading, onEditCapacity }: WorkersPanelProps) {
  const utils = trpc.useUtils();
  const roster = trpc.admin.rooms.list.useQuery(undefined, { refetchInterval: POLL_INTERVAL_MS });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** Which room's settings are expanded under the list. One at a time, as on the other tabs. */
  const [expanded, setExpanded] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<string>(NONE);

  const refresh = async () => {
    await Promise.all([
      utils.admin.rooms.list.invalidate(),
      utils.admin.listRooms.invalidate(),
      utils.admin.listProcesses.invalidate(),
    ]);
  };

  // The roster's own mutation, not a second one: pinning, unpinning and re-policying a
  // declaration are one procedure with one set of rules, reached from whichever page the
  // operator happens to be thinking on.
  const updateRoom = trpc.admin.rooms.update.useMutation({
    onSuccess: async () => {
      setExpanded(null);
      setAssigning(NONE);
      await refresh();
    },
  });
  // And the operations tab's, for a room that is running and has no declaration behind it.
  const updateLiveRoom = trpc.admin.updateRoomSettings.useMutation({
    onSuccess: async () => {
      setExpanded(null);
      await refresh();
    },
  });
  /**
   * **RESTART, WHICH IS `admin.closeRoom` AND NOT A NEW PROCEDURE.** It is the same mutation the
   * operations tab's Close is, reached from the room's own editor, because it is the answer to the
   * question that editor raises: two of the five settings are fixed for a running room, and
   * closing it is how the declaration behind it gets to apply them. A **declared** room comes back
   * under its own id with its declaration's settings the next time somebody is placed into it;
   * one with no declaration simply goes. See `wakeRosterRoom`.
   */
  const closeRoom = trpc.admin.closeRoom.useMutation({
    onSuccess: async () => {
      setExpanded(null);
      await refresh();
    },
  });

  const declarations = roster.data ?? [];
  // The first host until an operator picks one, so a single-host deployment is one click shorter.
  const selected = processes.find((row) => row.id === selectedId) ?? processes.at(0);

  /** The exact payload the Room roster tab sends, so the two pages cannot drift apart. */
  const pin = (row: RosterRow, workerId: string | null) =>
    updateRoom.mutate({
      id: row.room.id,
      name: row.room.name,
      workerId,
      settings: {},
      confirmUnsafe: false,
    });

  // The roster is half of every figure on this page — COMMITTED cannot be summed without it, and
  // a host drawn as under-committed because its declarations had not arrived yet would be the one
  // wrong reading this tab exists to prevent.
  if (isLoading || roster.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (processes.length === 0) {
    return <p className="text-sm text-muted-foreground">No room hosts are registered.</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        <strong className="font-semibold text-foreground">Committed</strong> is what the declared
        rooms pinned to a host would hold if they filled.{' '}
        <strong className="font-semibold text-foreground">Current</strong> is the humans seated on
        it right now, which is the figure its player ceiling is enforced against.{' '}
        <strong className="font-semibold text-foreground">Utilization</strong> is the share of its
        tick it is spending. A host can be over-committed with nothing running on it at all.
      </p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {processes.map((row) => (
          <WorkerCard
            key={row.id}
            row={row}
            allocation={allocationFor(row, declarations)}
            selected={selected?.id === row.id}
            onSelect={() => {
              setSelectedId(row.id);
              setExpanded(null);
              setAssigning(NONE);
            }}
          />
        ))}
      </div>

      {selected && (
        <WorkerDetail
          row={selected}
          allocation={allocationFor(selected, declarations)}
          assigned={assignedRooms(selected, declarations, rooms)}
          unassigned={declarations.filter((entry) => entry.room.workerId !== selected.id)}
          rosterError={roster.error?.message ?? null}
          saveError={
            updateRoom.error?.message ??
            updateLiveRoom.error?.message ??
            closeRoom.error?.message ??
            null
          }
          pending={updateRoom.isPending || updateLiveRoom.isPending}
          expanded={expanded}
          assigning={assigning}
          onAssigningChange={setAssigning}
          onExpand={setExpanded}
          onEditCapacity={() => onEditCapacity(selected)}
          onAssign={(roomId) => {
            const entry = declarations.find((candidate) => candidate.room.id === roomId);
            if (entry) pin(entry, selected.id);
          }}
          onPin={pin}
          onSaveDeclared={(entry, settings, confirmUnsafe) =>
            updateRoom.mutate({
              id: entry.room.id,
              name: entry.room.name,
              workerId: entry.room.workerId,
              settings,
              confirmUnsafe,
            })
          }
          onSaveLive={(roomId, settings) =>
            updateLiveRoom.mutate({ workerId: selected.id, roomId, settings })
          }
          onRestart={(roomId, workerId) => closeRoom.mutate({ workerId, roomId })}
          restarting={closeRoom.isPending}
        />
      )}
    </div>
  );
}

/**
 * COMMITTED for one host: the seats of every declaration PINNED to it, against the ceiling it
 * was measured at. Null limits — an unreachable host — leave the commitment summed and the
 * verdict withheld, which is what `hostAllocation` answers `unknown` for.
 */
function allocationFor(row: ProcessRow, declarations: readonly RosterRow[]): HostAllocation {
  return hostAllocation(
    declarations
      .filter((entry) => entry.room.workerId === row.id)
      .map((entry) => entry.room.settings),
    row.limits?.maxConcurrentPlayers ?? null,
  );
}

/**
 * Every room this host has anything to do with, pinned declarations first.
 *
 * Three kinds, and the distinction is the point: a pinned declaration is the operator's standing
 * commitment and is listed whether or not anything is running it; an auto declaration that
 * landed here and an overflow room the matchmaker invented are both here only until they go
 * idle. A declaration is matched by id, which is what a live room and its declaration share.
 */
function assignedRooms(
  row: ProcessRow,
  declarations: readonly RosterRow[],
  rooms: readonly RoomRow[],
): AssignedRoom[] {
  const declaredIds = new Set(declarations.map((entry) => entry.room.id));

  const pinned = declarations
    .filter((entry) => entry.room.workerId === row.id)
    .map<AssignedRoom>((entry) => ({
      key: `declared:${entry.room.id}`,
      id: entry.room.id,
      name: entry.room.name,
      kind: 'pinned',
      settings: entry.room.settings,
      runningOn: entry.runningOn,
      live: entry.live,
      declaration: entry,
    }));

  const here = rooms
    .filter((entry) => entry.workerId === row.id)
    // A pinned declaration running on its own host is already above, with its declaration's
    // settings — which is the copy an operator edits.
    .filter((entry) => !pinned.some((assigned) => assigned.id === entry.room.id))
    .map<AssignedRoom>((entry) => ({
      key: `live:${entry.room.id}`,
      id: entry.room.id,
      // The host's own name for it, which is the declaration's for a declared room and the
      // seeded one for an overflow room. `entry.name` is the roster's, and is null for the
      // second kind — it answers "was this declared", not "what is it called".
      name: entry.room.name,
      kind: declaredIds.has(entry.room.id) ? 'auto' : 'dynamic',
      settings: entry.room.settings,
      runningOn: entry.workerId,
      live: entry.room,
      // An auto declaration that landed here is still a declaration: its settings are editable
      // and it can be pinned to the host it is already on.
      declaration: declarations.find((row) => row.room.id === entry.room.id) ?? null,
    }));

  return [...pinned, ...here];
}

function WorkerCard({
  row,
  allocation,
  selected,
  onSelect,
}: {
  row: ProcessRow;
  allocation: HostAllocation;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <Card
      className={`${row.reachable ? '' : 'opacity-60 '}${selected ? 'border-primary' : ''}`}
      data-testid={`worker-card-${row.id}`}
    >
      <CardHeader className="gap-2 pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="break-all text-base">
            <button
              type="button"
              aria-pressed={selected}
              onClick={onSelect}
              className="cursor-pointer text-left text-primary hover:underline"
            >
              {row.id}
            </button>
          </CardTitle>
          <Pill tone="neutral">{row.embedded ? 'embedded' : 'remote'}</Pill>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Pill tone={row.reachable ? 'good' : 'bad'}>
            {row.reachable ? 'reachable' : 'unreachable'}
          </Pill>
          {row.draining && <Pill tone="warn">draining</Pill>}
          {row.reachable && !row.draining && (
            <Pill tone={row.accepting ? 'good' : 'warn'}>
              {row.accepting ? 'accepting' : 'not accepting'}
            </Pill>
          )}
          {/* A lock is a fact about the deployment, so it is drawn on an unreachable host too. */}
          {row.locked && <Pill tone="warn">locked</Pill>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <AllocationReadout allocation={allocation} />

        <dl className="grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
          <Figure
            label="Committed"
            testId={`committed-${row.id}`}
            value={allocation.committedSeats.toLocaleString()}
            note={`${allocation.pinnedRooms} pinned`}
          />
          <Figure
            label="Current"
            testId={`current-${row.id}`}
            value={row.humanCount === null ? '—' : row.humanCount.toLocaleString()}
            note="humans seated"
          />
          <Figure
            label="Utilization"
            testId={`utilization-${row.id}`}
            value={row.utilization === null ? '—' : `${(row.utilization * 100).toFixed(0)}%`}
            note="of the tick"
            alarming={row.utilization !== null && row.utilization >= UTILIZATION_ALARM}
          />
          <Figure label="Live rooms" value={formatCount(row.roomCount)} />
          <Figure label="Bots" value={formatCount(row.botCount)} />
          <Figure label="Uptime" value={row.uptimeMs === null ? '—' : formatAge(row.uptimeMs)} />
          <Figure label="Version" value={row.version ?? '—'} />
          <Figure label="Worker id" value={row.workerId ?? '—'} />
          <Figure
            label="Ceiling"
            value={row.limits === null ? '—' : row.limits.maxConcurrentPlayers.toLocaleString()}
            note="players"
          />
        </dl>
      </CardContent>
    </Card>
  );
}

const VERDICT_TONE: Record<HostAllocation['verdict'], 'good' | 'warn' | 'bad' | 'neutral'> = {
  unknown: 'neutral',
  'under-committed': 'warn',
  'well-allocated': 'good',
  'over-committed': 'bad',
};

const VERDICT_LABEL: Record<HostAllocation['verdict'], string> = {
  unknown: 'Commitment unjudged',
  'under-committed': 'Under-committed',
  'well-allocated': 'Well allocated',
  'over-committed': 'OVER-COMMITTED',
};

/**
 * COMMITTED against the measured ceiling, in one line and one bar.
 *
 * The bar is the committed share of the ceiling, so it fills as declarations are pinned rather
 * than as players arrive — that is the distinction the whole tab is about, and a bar that moved
 * with occupancy would be a third drawing of UTILIZATION.
 */
function AllocationReadout({ allocation }: { allocation: HostAllocation }) {
  const { committedSeats, ceiling, share, verdict } = allocation;
  const percent = share === null ? 0 : Math.min(100, Math.max(0, share * 100));

  return (
    <div className="space-y-1.5" data-testid="allocation">
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 text-sm">
        <Pill tone={VERDICT_TONE[verdict]}>{VERDICT_LABEL[verdict]}</Pill>
        <span className="tabular-nums text-muted-foreground">
          {ceiling === null
            ? `${committedSeats.toLocaleString()} seats committed, ceiling unknown`
            : `${committedSeats.toLocaleString()} of ${ceiling.toLocaleString()} seats committed`}
        </span>
      </div>
      {/* Decorative: the sentence above it is the accessible copy of the same number. */}
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
        <div
          className={`h-full rounded-full ${verdict === 'over-committed' ? 'bg-destructive' : 'bg-primary'}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function WorkerDetail({
  row,
  allocation,
  assigned,
  unassigned,
  rosterError,
  saveError,
  pending,
  expanded,
  assigning,
  onAssigningChange,
  onExpand,
  onEditCapacity,
  onAssign,
  onPin,
  onSaveDeclared,
  onSaveLive,
  onRestart,
  restarting,
}: {
  row: ProcessRow;
  allocation: HostAllocation;
  assigned: readonly AssignedRoom[];
  /** Declarations pinned somewhere else or on Auto — the ones this host can be given. */
  unassigned: readonly RosterRow[];
  rosterError: string | null;
  saveError: string | null;
  pending: boolean;
  expanded: string | null;
  assigning: string;
  onAssigningChange: (roomId: string) => void;
  onExpand: (key: string | null) => void;
  onEditCapacity: () => void;
  onAssign: (roomId: string) => void;
  /** Null unpins to Auto, a host id pins — `admin.rooms.update`'s own three-valued field. */
  onPin: (entry: RosterRow, workerId: string | null) => void;
  onSaveDeclared: (entry: RosterRow, settings: RoomSettingsPatch, confirmUnsafe: boolean) => void;
  onSaveLive: (roomId: string, settings: RoomSettingsPatch) => void;
  /**
   * Close the running room so it comes back from its declaration — see `closeRoom`.
   *
   * The host is passed rather than assumed to be the one selected: where a room is PINNED and
   * where it is RUNNING are different facts (there is no live handover, so a re-pinned room stays
   * where it opened), and `admin.closeRoom` has to be told the host that actually holds it.
   */
  onRestart: (roomId: string, workerId: string) => void;
  restarting: boolean;
}) {
  const declarationsById = new Map(unassigned.map((entry) => [entry.room.id, entry]));

  return (
    <Card>
      <CardHeader className="gap-2 pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="break-all text-base">Rooms assigned to {row.id}</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {row.locked && (
              <span className="text-xs text-muted-foreground">
                Locked: it opens no room the matchmaker invents, and still wakes these.
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={row.limits === null}
              onClick={onEditCapacity}
            >
              Capacity &amp; lock
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {allocation.verdict === 'over-committed'
            ? `These pinned rooms would hold ${allocation.committedSeats.toLocaleString()} players between them, ` +
              `above the ${(allocation.ceiling ?? 0).toLocaleString()} this host will serve. It refuses the seat past ` +
              'its ceiling whatever the declarations say, so the rooms past it are ones nobody can be placed into.'
            : 'Pinned rooms open only here. A room that is already running stays on the host it opened on until it goes empty — there is no live handover.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {rosterError && (
          <p className="text-sm text-destructive" role="alert">
            {rosterError}
          </p>
        )}

        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1.5">
            <label htmlFor="worker-assign" className="text-sm font-medium">
              Assign a declared room
            </label>
            <select
              id="worker-assign"
              value={assigning}
              onChange={(event) => onAssigningChange(event.target.value)}
              className="h-9 w-full min-w-56 cursor-pointer rounded-md border border-input bg-background px-3 py-1 text-sm"
            >
              <option value={NONE}>Pick a room…</option>
              {unassigned.map((entry) => (
                <option key={entry.room.id} value={entry.room.id}>
                  {entry.room.name} ({entry.room.workerId ?? 'auto'},{' '}
                  {entry.room.settings.maxPlayers} seats)
                </option>
              ))}
            </select>
          </div>
          <Button
            size="sm"
            disabled={assigning === NONE || pending || !declarationsById.has(assigning)}
            onClick={() => onAssign(assigning)}
          >
            Assign
          </Button>
          {unassigned.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Nothing left to assign — every declared room is already pinned here. Declare more on
              Room roster.
            </p>
          )}
        </div>

        {saveError && (
          <p className="text-sm text-destructive" role="alert">
            {saveError}
          </p>
        )}

        {assigned.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            Nothing is declared for this host and nothing is running on it.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Room</th>
                  <th className="py-2 pr-3 font-medium">Assignment</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 text-right font-medium">Humans / bots / seats</th>
                  <th className="py-2 font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {assigned.map((entry) => {
                  const open = expanded === entry.key;
                  return (
                    <RoomRows
                      key={entry.key}
                      entry={entry}
                      host={row}
                      open={open}
                      pending={pending}
                      declaration={entry.declaration}
                      onToggle={() => onExpand(open ? null : entry.key)}
                      onPin={onPin}
                      onSaveDeclared={onSaveDeclared}
                      onSaveLive={onSaveLive}
                      onRestart={onRestart}
                      restarting={restarting}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RoomRows({
  entry,
  host,
  open,
  pending,
  declaration,
  onToggle,
  onPin,
  onSaveDeclared,
  onSaveLive,
  onRestart,
  restarting,
}: {
  entry: AssignedRoom;
  host: ProcessRow;
  open: boolean;
  pending: boolean;
  declaration: RosterRow | null;
  onToggle: () => void;
  onPin: (entry: RosterRow, workerId: string | null) => void;
  onSaveDeclared: (entry: RosterRow, settings: RoomSettingsPatch, confirmUnsafe: boolean) => void;
  onSaveLive: (roomId: string, settings: RoomSettingsPatch) => void;
  onRestart: (roomId: string, workerId: string) => void;
  restarting: boolean;
}) {
  return (
    <>
      <tr className="border-b border-border/60">
        <td className="py-2 pr-3 font-medium">
          <button
            type="button"
            aria-expanded={open}
            onClick={onToggle}
            className="cursor-pointer text-left font-semibold text-primary hover:underline"
          >
            {entry.name}
          </button>
          <span className="block break-all text-xs text-muted-foreground">{entry.id}</span>
        </td>
        <td className="py-2 pr-3">
          {entry.kind === 'pinned' ? (
            <Pill tone="good">pinned here</Pill>
          ) : entry.kind === 'auto' ? (
            <Pill tone="neutral">declared · auto</Pill>
          ) : (
            <Pill tone="neutral">dynamic</Pill>
          )}
        </td>
        <td className="py-2 pr-3 text-muted-foreground">
          {entry.live === null
            ? 'idle'
            : entry.runningOn === host.id
              ? 'running here'
              : `running on ${entry.runningOn}`}
        </td>
        <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
          {entry.live === null
            ? `— / — / ${entry.settings.maxPlayers}`
            : `${entry.live.humanCount} / ${entry.live.botCount} / ${entry.live.maxPlayers}`}
        </td>
        <td className="py-2">
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onToggle}
              aria-label={`Settings for ${entry.name}`}
            >
              {open ? 'Hide settings' : 'Settings'}
            </Button>
            {/* Unpinning is what "unassign" means: the declaration goes back to Auto and the
                matchmaker chooses on the join that next wakes it. A declaration that merely
                landed here on Auto is not assigned to anything, so it is offered the pin
                instead — both are the one three-valued field on `admin.rooms.update`. */}
            {declaration !== null && entry.kind === 'pinned' && (
              <Button
                variant="outline"
                size="sm"
                disabled={pending}
                onClick={() => onPin(declaration, null)}
                aria-label={`Unassign ${entry.name} from ${host.id}`}
              >
                Unassign
              </Button>
            )}
            {declaration !== null && entry.kind === 'auto' && (
              <Button
                variant="outline"
                size="sm"
                disabled={pending}
                onClick={() => onPin(declaration, host.id)}
                aria-label={`Pin ${entry.name} to ${host.id}`}
              >
                Pin here
              </Button>
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-border/60 bg-muted/30">
          <td colSpan={5} className="p-4">
            {declaration === null ? (
              <>
                <p className="mb-3 text-sm text-muted-foreground">
                  This room is running now and has no declaration behind it. Saving applies to it
                  immediately; its world size and its mode are fixed, because the room was built
                  inside those walls and its match loop was decided when it opened.
                </p>
                <SettingsForm
                  settings={entry.settings}
                  serverError={null}
                  pending={pending}
                  worldSizeEditable={false}
                  modeEditable={false}
                  submitLabel="Save settings"
                  cancelLabel="Close"
                  onSubmit={(settings) => onSaveLive(entry.id, settings)}
                  onClose={onToggle}
                />
              </>
            ) : (
              <>
                <p className="mb-3 text-sm text-muted-foreground">
                  These are the settings the room is <strong>started</strong> with, its world size
                  and its mode included. A copy that is running right now keeps what it opened with;
                  saving here decides the next time it opens.
                </p>
                {entry.runningOn !== null && (
                  <RestartNote
                    pending={restarting}
                    onRestart={() => onRestart(entry.id, entry.runningOn as string)}
                  />
                )}
                <SettingsForm
                  settings={declaration.room.settings}
                  serverError={null}
                  pending={pending}
                  submitLabel="Save room"
                  cancelLabel="Close"
                  onSubmit={(settings, confirmUnsafe) =>
                    onSaveDeclared(declaration, settings, confirmUnsafe)
                  }
                  onClose={onToggle}
                />
              </>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Figure({
  label,
  value,
  note,
  testId,
  alarming = false,
}: {
  label: string;
  value: string;
  note?: string;
  /** Set on the three figures point 6 is about, so a test can prove they are three figures. */
  testId?: string;
  alarming?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd
        className={`break-all font-semibold tabular-nums ${alarming ? 'text-destructive' : ''}`}
        data-testid={testId}
      >
        {value}
      </dd>
      {note && <dd className="text-xs text-muted-foreground">{note}</dd>}
    </div>
  );
}

const PILL_TONES = {
  good: 'bg-primary/15 text-primary',
  warn: 'bg-accent/15 text-accent',
  bad: 'bg-destructive/15 text-destructive',
  neutral: 'bg-muted text-muted-foreground',
} as const;

function Pill({ tone, children }: { tone: keyof typeof PILL_TONES; children: ReactNode }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold ${PILL_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

function formatCount(value: number | null): string {
  return value === null ? '—' : value.toLocaleString();
}

function formatAge(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/**
 * What to do about the settings a running room will not take — and the button that does it.
 *
 * **THE ANSWER TO "WHEN DOES THIS TAKE EFFECT" IS DIFFERENT PER FIELD, AND THAT IS WHY IT IS SAID
 * HERE RATHER THAN LEFT TO BE WORKED OUT.** Saving a declaration writes what the room OPENS with;
 * the bot mix and the access rules are also applied to the copy that is running, because those are
 * read where a player is placed, but the mode and the walls are not — the match loop and every
 * body's position were decided when it opened. Andy: "I need to know if I change room settings,
 * that they take effect immediately or on next deathmatch start or something."
 *
 * Restart is `admin.closeRoom`, which is the same mutation the operations tab's Close is: it ends
 * the matches in the room, and a **declared** room then comes back under its own id with this
 * declaration's settings the next time somebody is placed into it. It is deliberately not a new
 * procedure and deliberately not automatic — a save that silently threw everybody out of a live
 * room would be the worst button on this page.
 */
function RestartNote({ pending, onRestart }: { pending: boolean; onRestart: () => void }) {
  return (
    <div
      data-testid="restart-note"
      className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/40 p-3"
    >
      <p className="text-xs text-muted-foreground">
        This room is running. Saving changes the bot mix and the access rules on the live copy too;
        the <strong>mode</strong> and the <strong>world size</strong> only apply the next time it
        opens. Restart to apply them now — it ends the matches being played in it, and the room
        comes back under the same id with these settings.
      </p>
      <Button variant="outline" size="sm" disabled={pending} onClick={onRestart}>
        {pending ? 'Restarting…' : 'Restart room'}
      </Button>
    </div>
  );
}
